/**
 * API helpers for Super Admin organization management.
 * Proxies to /api/admin/organizations (Heimdall) with the given idToken.
 */

import type {
  OrganizationListResponse,
  OrganizationDTO,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
} from '@/shared/types';

export async function listOrganizations(
  idToken: string,
  skip: number = 0,
  limit: number = 100
): Promise<OrganizationListResponse> {
  const response = await fetch(
    `/api/admin/organizations?skip=${encodeURIComponent(String(skip))}&limit=${encodeURIComponent(String(limit))}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}

export async function createOrganization(
  idToken: string,
  body: CreateOrganizationRequest
): Promise<OrganizationDTO> {
  const response = await fetch('/api/admin/organizations', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}

export async function getOrganization(
  idToken: string,
  orgId: string
): Promise<OrganizationDTO> {
  const response = await fetch(`/api/admin/organizations/${encodeURIComponent(orgId)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}

export async function updateOrganization(
  idToken: string,
  orgId: string,
  body: UpdateOrganizationRequest
): Promise<OrganizationDTO> {
  const response = await fetch(`/api/admin/organizations/${encodeURIComponent(orgId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}

export async function updateCurrentOrganization(
    idToken: string,
    orgId: string
): Promise<OrganizationDTO> {
  const response = await fetch(`/api/admin/organizations/pool-state`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ organization_id: orgId }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}

/**
 * Mark the current tenant pool organization as ready for tenants (knowledge_base_ready=true).
 * No body. Requires a current organization to be set in tenant pool state.
 */
export async function setTenantPoolStateReady(idToken: string): Promise<unknown> {
  const response = await fetch(`/api/admin/organizations/pool-state/ready`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}

export async function setTenantPoolStateNotReady(idToken: string): Promise<unknown> {
  const response = await fetch(`/api/admin/organizations/pool-state/ready`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(err.detail || err.error || response.statusText);
  }

  return response.json();
}